import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Spin,
  Result,
  Popconfirm,
  message,
  Typography,
  Divider,
  Space,
  Tooltip,
  Alert,
} from "antd";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  CreditCardOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  StarOutlined,
  StarFilled,
  LockOutlined,
  LoadingOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import {
  APP_ENV,
  pdGetCustomerCards,
  pdAddCard,
  pdUpdateCard,
  pdDeleteCard,
  pdSetDefaultCard,
} from "./../services";

// ── Load Stripe once (replace with your publishable key) ──────────────────
const stripePromise = loadStripe(APP_ENV === "PRODUCTION" ? "pk_live_51KcCbmAApC6judFcFkZNFrFJ6DlafUcWAAdwCoodJB5RgNO4ie2MVIEWHjZ0S2NL1PrZwuGOPKQTu0AjpZcfbZWF00TOiNE43A" : "pk_test_51KcCbmAApC6judFcKqZTAxMBwlPH8ayLzdBusarcMrsFHzdDK3srFDbsKzxsKRxw1Mg31qZlnQZ6JVTXQ8H42tRi00f7JI1ggm");

const { Title, Text } = Typography;
const { Option } = Select;

// ─── Card brand → color map ────────────────────────────────────────────────
const BRAND_COLOR = {
  visa: "#1a1f71",
  mastercard: "#eb001b",
  amex: "#007bc1",
  discover: "#f76f20",
  default: "#434343",
};

const MONTHS = [
  "01","02","03","04","05","06",
  "07","08","09","10","11","12",
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 12 }, (_, i) => String(currentYear + i));

// ─── Small visual card tile ─────────────────────────────────────────────────
const CardTile = ({ card, onEdit, onDelete, onSetDefault, totalCards }) => {
  const brand = (card.brand || "default").toLowerCase();
  const color = BRAND_COLOR[brand] || BRAND_COLOR.default;

  return (
    <Card
      style={{
        borderRadius: 14,
        background: `linear-gradient(135deg, ${color} 0%, #2d2d2d 100%)`,
        color: "#fff",
        minHeight: 160,
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        marginBottom: 16,
      }}
      bodyStyle={{ padding: "18px 22px" }}
    >
      {/* Brand + default badge */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Text strong style={{ color: "#fff", fontSize: 18, textTransform: "capitalize" }}>
          {card.brand || "Card"}  <Space>
          {card.is_default && (
            <Tag color="gold" style={{ borderRadius: 20 }}>
              <StarFilled /> Default
            </Tag>
          )}
        </Space>
        </Text>
       
      </div>

      {/* Masked number */}
      <div style={{ marginTop: 20, letterSpacing: 4, fontSize: 16, color: "#e0e0e0" }}>
        •••• •••• •••• {card.last4}
      </div>

      {/* Expiry + name */}
      <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between" }}>
        <div>
          <Text style={{ color: "#bbb", fontSize: 11 }}>EXPIRES</Text>
          <br />
          <Text style={{ color: "#fff" }}>
            {String(card.exp_month).padStart(2, "0")} / {card.exp_year}
          </Text>
        </div>
        <div style={{ textAlign: "right" }}>
          <Text style={{ color: "#bbb", fontSize: 11 }}>CARDHOLDER</Text>
          <br />
          <Text style={{ color: "#fff" }}>{card.name || "—"}</Text>
        </div>
      </div>

      {/* Action buttons */}
      <div
        style={{
          position: "absolute",
          top: 14,
          right: 14,
          display: "flex",
          gap: 8,
        }}
      >
        {!card.is_default && (
          <Tooltip title="Set as default">
            <Button
              size="small"
              shape="circle"
              icon={<StarOutlined />}
              style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff" }}
              onClick={() => onSetDefault(card)}
            />
          </Tooltip>
        )}
        <Tooltip title="Edit card">
          <Button
            size="small"
            shape="circle"
            icon={<EditOutlined />}
            style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff" }}
            onClick={() => onEdit(card)}
          />
        </Tooltip>
        <Tooltip title={totalCards <= 1 ? "Add another card before removing this one" : "Remove card"}>
          {totalCards <= 1 ? (
            <Button
              size="small"
              shape="circle"
              icon={<DeleteOutlined />}
              disabled
              style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "rgba(255,255,255,0.3)", cursor: "not-allowed" }}
            />
          ) : (
            <Popconfirm
              title="Remove this card?"
              description="This card will be permanently detached from your account."
              onConfirm={() => onDelete(card)}
              okText="Remove"
              okButtonProps={{ danger: true }}
            >
              <Button
                size="small"
                shape="circle"
                icon={<DeleteOutlined />}
                style={{ background: "rgba(255,0,0,0.25)", border: "none", color: "#fff" }}
              />
            </Popconfirm>
          )}
        </Tooltip>
      </div>
    </Card>
  );
};

// ─── Stripe CardElement styles ───────────────────────────────────────────────
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "15px",
      color: "#1a1a2e",
      fontFamily: "'Inter', sans-serif",
      "::placeholder": { color: "#aab7c4" },
      iconColor: "#722ed1",
    },
    invalid: { color: "#ff4d4f", iconColor: "#ff4d4f" },
  },
  hidePostalCode: true,
};

// ─── Inner form (needs to be inside <Elements>) ──────────────────────────────
const AddCardForm = ({ onClose, onSaved, email }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [cardError, setCardError] = useState("");

  const handleSubmit = async (values) => {
    if (!stripe || !elements) return;
    setSaving(true);
    setCardError("");
    try {
      const cardEl = elements.getElement(CardElement);
      // Tokenize card details securely in the browser → get paymentMethod id
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardEl,
        billing_details: { name: values.name, email },
      });

      if (error) {
        setCardError(error.message);
        return;
      }

      // Send only the paymentMethod.id (token) to your backend — no raw card data
      const result = await pdAddCard({ payment_method_id: paymentMethod.id, email });
      if (result?.status === "error") {
        setCardError(result.message || "Failed to add card");
        return;
      }
      message.success("Card added successfully");
      onSaved();
      onClose();
    } catch (err) {
      setCardError(err?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      {/* Cardholder name */}
      <Form.Item
        name="name"
        label="Cardholder Name"
        rules={[{ required: true, message: "Enter cardholder name" }]}
      >
        <Input prefix={<CreditCardOutlined />} placeholder="John Doe" />
      </Form.Item>

      {/* Stripe secure card element */}
      <Form.Item label="Card Details">
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            padding: "10px 12px",
            background: "#fff",
            transition: "border-color 0.2s",
          }}
        >
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            onChange={(e) => setCardError(e.error ? e.error.message : "")}
          />
        </div>
        {cardError && (
          <Alert
            type="error"
            message={cardError}
            style={{ marginTop: 8, borderRadius: 6 }}
            showIcon
          />
        )}
        <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
          <LockOutlined style={{ color: "#52c41a", fontSize: 12 }} />
          <Text type="secondary" style={{ fontSize: 11 }}>
            Secured by <strong style={{ color: "#635bff" }}>Stripe</strong> — we never see your card number
          </Text>
        </div>
      </Form.Item>

      <Divider />

      <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            disabled={!stripe}
            style={{ background: "#722ed1", borderColor: "#722ed1" }}
          >
            Add Card
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

// ─── Edit card modal (only name + expiry — no re-entry of card number) ───────
const EditCardForm = ({ editCard, onClose, onSaved, email }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editCard) {
      form.setFieldsValue({
        name: editCard.name || "",
        exp_month: String(editCard.exp_month).padStart(2, "0"),
        exp_year: String(editCard.exp_year),
      });
    }
  }, [editCard, form]);

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      await pdUpdateCard({ card_id: editCard.id, email, ...values });
      message.success("Card updated successfully");
      onSaved();
      onClose();
    } catch (err) {
      message.error(err?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <Form.Item
        name="name"
        label="Cardholder Name"
        rules={[{ required: true, message: "Enter cardholder name" }]}
      >
        <Input placeholder="John Doe" />
      </Form.Item>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="exp_month" label="Exp. Month" rules={[{ required: true, message: "Required" }]}>
            <Select placeholder="MM">
              {MONTHS.map((m) => <Option key={m} value={m}>{m}</Option>)}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="exp_year" label="Exp. Year" rules={[{ required: true, message: "Required" }]}>
            <Select placeholder="YYYY">
              {YEARS.map((y) => <Option key={y} value={y}>{y}</Option>)}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={saving}
            style={{ background: "#722ed1", borderColor: "#722ed1" }}>
            Save Changes
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

// ─── Unified Add / Edit Modal ────────────────────────────────────────────────
const CardModal = ({ visible, editCard, onClose, onSaved }) => {
  const isEdit = !!editCard;
  const AuthData = JSON.parse(localStorage.getItem("auth_data"));
  const email = AuthData?.email || "";

  return (
    <Modal
      open={visible}
      title={
        <Space>
          <CreditCardOutlined />
          {isEdit ? "Edit Card" : "Add New Card"}
        </Space>
      }
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      {isEdit ? (
        <EditCardForm
          editCard={editCard}
          onClose={onClose}
          onSaved={onSaved}
          email={email}
        />
      ) : (
        // Wrap ADD form in Stripe <Elements> provider
        <Elements stripe={stripePromise}>
          <AddCardForm onClose={onClose} onSaved={onSaved} email={email} />
        </Elements>
      )}
    </Modal>
  );
};

// ─── Main BillingPage ────────────────────────────────────────────────────────
const BillingPage = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editCard, setEditCard] = useState(null);
const AuthData = JSON.parse(localStorage.getItem("auth_data"));  
  const email = AuthData?.email || "";

  const fetchCards = async () => {
    setLoading(true);
    try {
      const { status, data } = await pdGetCustomerCards({ email });
      if (status === "success") {
        setCards(data || []);
      } else {
        message.error("Failed to load payment methods");
      }
    } catch {
      message.error("Network error while fetching cards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAdd = () => {
    setEditCard(null);
    setModalVisible(true);
  };

  const openEdit = (card) => {
    setEditCard(card);
    setModalVisible(true);
  };

  const handleDelete = async (card) => {
    try {
      await pdDeleteCard({ card_id: card.id, email });
      message.success("Card removed");
      fetchCards();
    } catch {
      message.error("Failed to remove card");
    }
  };

  const handleSetDefault = async (card) => {
    try {
      await pdSetDefaultCard({ card_id: card.id, email });
      message.success("Default card updated");
      fetchCards();
    } catch {
      message.error("Failed to update default card");
    }
  };

  // ── Full-screen centered loading ──────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 24,
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 64, color: "#722ed1" }} spin />} />
        <div style={{ textAlign: "center" }}>
          <Title level={4} style={{ margin: 0, color: "#722ed1" }}>
            Please Wait…
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Fetching your payment details
          </Text>
        </div>
      </div>
    );
  }

  // ── Empty state (no cards) ───────────────────────────────────────────────
  if (!loading && cards.length === 0) {
    return (
      <div style={{ padding: "24px 0" }}>
        {/* Header strip */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <CreditCardOutlined style={{ marginRight: 10, color: "#722ed1" }} />
              Payment Methods
            </Title>
            {email && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                Account: {email}
              </Text>
            )}
          </Col>
         <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openAdd}
              style={{ borderRadius: 8, background: "#722ed1", borderColor: "#722ed1" }}
            >
              Add Card
            </Button>
          </Col> 
        </Row>

        <Card
          style={{
            borderRadius: 16,
            border: "1.5px dashed #d9d9d9",
            background: "#fafafa",
            minHeight: 380,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          bodyStyle={{ width: "100%", padding: 0 }}
        >
          <Result
            icon={
              <CreditCardOutlined
                style={{ fontSize: 80, color: "#d9d9d9" }}
              />
            }
            title="No Payment Methods Attached"
            subTitle={
              <>
                <div style={{ marginBottom: 6 }}>
                  We couldn't find any saved cards for
                  <strong> {email || "your account"}</strong>.
                </div>
                <div style={{ color: "#8c8c8c", fontSize: 13 }}>
                  Add a credit or debit card to manage your subscription payments.
                </div>
              </>
            }
            // extra={
            //   <Button
            //     type="primary"
            //     size="large"
            //     icon={<PlusOutlined />}
            //     onClick={openAdd}
            //     style={{
            //       borderRadius: 8,
                 
            //       paddingInline: 32,
            //     }}
            //   >
            //     Add Your First Card
            //   </Button>
            // }
          />
        </Card>

        <CardModal
          visible={modalVisible}
          editCard={editCard}
          onClose={() => setModalVisible(false)}
          onSaved={fetchCards}
        />
      </div>
    );
  }

  // ── Main content — Stripe-style layout ──────────────────────────────────
  return (
    <div style={{ padding: "24px 0" }}>

      {/* ── Page header ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 28 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <CreditCardOutlined style={{ marginRight: 10, color: "#722ed1" }} />
            Payment Methods
          </Title>
          {email && (
            <Tag color="purple" style={{ fontSize: 15 }}>  Account: {email}  </Tag>
          )}
        </Col>
        <Col>
          {/* <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openAdd}
            style={{ borderRadius: 8,  }}
          >
            Add Card
          </Button> */}
        </Col>
      </Row>

      {/* ── Stripe-style outer card container ── */}
      <Card
        style={{
          borderRadius: 16,
          boxShadow: "0 2px 16px rgba(114,46,209,0.07)",
          border: "1px solid #f0e6ff",
          background: "#fff",
          padding: 0,
        }}
        bodyStyle={{ padding: "24px 28px" }}
      >
        {/* Stripe-style section label */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 20,
            paddingBottom: 14,
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <SafetyCertificateOutlined style={{ color: "#52c41a", fontSize: 18 }} />
          <Text strong style={{ fontSize: 15 }}>Saved Cards</Text>
          <Tag
            color="purple"
            style={{ marginLeft: 8, borderRadius: 20, fontSize: 12 }}
          >
            {cards.length} {cards.length === 1 ? "card" : "cards"}
          </Tag>
        </div>

        {/* ── Card grid ── */}
        <Row gutter={[16, 16]}>
          {cards.map((card) => (
            <Col xs={24} sm={24} md={12} lg={8} key={card.id}>
              <CardTile
                card={card}
                onEdit={openEdit}
                onDelete={handleDelete}
                onSetDefault={handleSetDefault}
                totalCards={cards.length}
              />
            </Col>
          ))}

          {/* ── Add card tile ── */}
          <Col xs={24} sm={24} md={12} lg={8}>
            <Card
              onClick={openAdd}
              style={{
                borderRadius: 14,
                border: "2px dashed #d3adf7",
                minHeight: 160,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#faf5ff",
                transition: "border-color 0.2s, background 0.2s",
              }}
              bodyStyle={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: 24,
              }}
              hoverable
            >
              <PlusOutlined style={{ fontSize: 32, color: "#722ed1" }} />
              <Text style={{ color: "#722ed1", fontWeight: 600, fontSize: 14 }}>
                Add New Card
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Stripe-style secure badge */}
        <div
          style={{
            marginTop: 24,
            paddingTop: 14,
            borderTop: "1px solid #f0f0f0",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <LockOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Payments are secured and encrypted by{" "}
            <strong style={{ color: "#635bff" }}>Stripe</strong>. Your card
            details are never stored on our servers.
          </Text>
        </div>
      </Card>

      {/* ── Add / Edit modal ── */}
      <CardModal
        visible={modalVisible}
        editCard={editCard}
        onClose={() => setModalVisible(false)}
        onSaved={fetchCards}
      />
    </div>
  );
};

export default BillingPage;
